import { Component, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { ExploreState } from '../../state/explore.state';
import { Observable, Subject } from 'rxjs';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { Load, SortSearchResults, ResetSearchResults, CheckSearchResult, SetCheckedSearchResults, SelectCategory } from '../../state/explore.actions';
import { BookmarkActionsEnum } from 'src/app/bookmarks/components/bookmark-actions/bookmark-actions.component';
import { BookmarkSortModel } from 'src/app/bookmarks/models/bookmark-sort.model';
import { takeUntil } from 'rxjs/operators';
import { GalleryViewMode } from 'src/app/gallery/models/gallery-view-mode.enum';
import { GalleryState } from 'src/app/gallery/state/gallery.state';
import { StoryType } from 'src/app/story/models/story-type.enum';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { DialogActions } from 'src/app/dialogs/state/dialog.actions';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';

@Component({
  selector: 'app-explore',
  templateUrl: './explore.component.html',
  styleUrls: ['./explore.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExploreComponent implements OnInit, OnDestroy {
  galleryViewMode = GalleryViewMode;
 
  @Select(ExploreState.getSearchResults)
  searchResults$: Observable<BookmarkModel[]>;
  
  @Select(ExploreState.getSort)
  sort$: Observable<BookmarkSortModel>;

  @Select(ExploreState.getSelectedCategory)
  selectedCategory$: Observable<string>;

  @Select(ExploreState.getLoading)
  loading$: Observable<boolean>;

  @Select(GalleryState.getViewMode)
  viewMode$: Observable<GalleryViewMode>;
  
  @Select(AuthState.isPremiumUser)
  isPremiumUser$: Observable<boolean>;


  private ngUnsubscribe = new Subject();

  constructor(
    private route: ActivatedRoute,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    private router: Router
  ) { }

  ngOnInit() {
    
     // load by category
     this.route.params
     .pipe(
       takeUntil(this.ngUnsubscribe)
     )
     .subscribe((params) => {
       const category = params.category;
       this.loadByCategory(category);
     });

   this.searchResults$
     .pipe(
       takeUntil(this.ngUnsubscribe)
     )
     .subscribe((results: BookmarkModel[]) => {
       this.store.dispatch(new SetCheckedSearchResults([]));
       this.changeDetectorRef.markForCheck();
     });
  }

  ngOnDestroy() {
    this.store.dispatch(new ResetSearchResults());
    this.store.dispatch(new SelectCategory(null));

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  loadByCategory(category: string) {
    this.store.dispatch(new ResetSearchResults());
    this.store.dispatch(new SelectCategory(category));
    this.store.dispatch(new Load(category));
  }

  handleBookmarksSort(sort: BookmarkSortModel) {
    this.store.dispatch(new SortSearchResults(sort));
  }

  handleCheckChanged(checked: boolean, id: string) {
    this.setChecked(checked, id);
  }

  setChecked(checked: boolean, id: string) {
    this.store.dispatch(new CheckSearchResult(id, checked));
  }

  handleSelectAllClick() {
    this.selectAll();
  }

  handleClearSelectionClick() {
    this.store.dispatch(new SetCheckedSearchResults([]));
  }

  handleSelectBookmark(bookmark: BookmarkModel) {
    if (!bookmark.premium || this.store.selectSnapshot(AuthState.isPremiumUser)) {
      this.router.navigate(['/view'], { queryParams: { id: bookmark.id, type: StoryType.Module }});
    } else {
      this.store.dispatch(new DialogActions.OpenPremiumContent());
    }
  }
  
  selectAll() {
    const ids = this.store.selectSnapshot(ExploreState.getSearchResults)
      .map((bookmark) => bookmark.id);

    this.store.dispatch(new SetCheckedSearchResults(ids));
  }

  get bookmarkActions(): BookmarkActionsEnum[] {
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);

    // [bookmarkActions.View, bookmarkActions.CopyLink, bookmarkActions.CopyBookmark, bookmarkActions.Edit, bookmarkActions.Share]

    return [
      BookmarkActionsEnum.View,
      BookmarkActionsEnum.Edit,
      BookmarkActionsEnum.CopyLink,
      BookmarkActionsEnum.CopyBookmark,
      // add optional organization actions
      ...organization ? [BookmarkActionsEnum.CopyTeamBookmark] : []
    ];
  }
}
